import { endJobAssignmentReasons } from '../EndJobAssignmentModal/structure'
import { fetchCancellableBookings, fetchShiftOverbookCover } from 'syft-acp-core/api/resources/bookings'

import {
  BookingCompensationDecisions,
  CancelReason,
  CancelStep,
  ShiftOverbookCover,
  SpotDecision,
  WorkerJobBooking,
} from './types'

export const MODAL_NAME = 'bulkCancelBookingModal'
export const OTHER_LABELS = ['Other - Involuntary', 'Other - Voluntary']
export const WINDOW_24H_MS = 24 * 60 * 60 * 1000
export const FLEXER_TERMS_URLS = {
  US: 'https://indeedflex.com/legal/flexer-terms-and-conditions/',
  GB: 'https://indeedflex.co.uk/legal/flexer-terms/',
} as const
// Product policy: these cancellation reasons start late shifts with CH selected.
// Use stable values so changing the words shown to admins cannot change the policy.
export const CH_DEFAULT_REASON_VALUES = ['reduced_temp_worker_demand']

export const isOtherReason = (reason: CancelReason) =>
  typeof reason === 'string' && OTHER_LABELS.includes(reason)

// UK and US share the same CH behavior; only the legal document link changes.
export const flexerTermsURLFor = (countryCode: string) =>
  countryCode === 'US' ? FLEXER_TERMS_URLS.US : FLEXER_TERMS_URLS.GB

export const startMsOf = (b: WorkerJobBooking) =>
  typeof b.start_time === 'number' ? b.start_time : Date.parse(b.start_time)

const reasonOptionFor = (reason: CancelReason) => {
  if (typeof reason === 'number' || (typeof reason === 'string' && /^\d+$/.test(reason))) {
    return endJobAssignmentReasons.find(option => option.id === Number(reason))
  }

  return typeof reason === 'string'
    ? endJobAssignmentReasons.find(option => option.value === reason || option.label === reason)
    : undefined
}

// Dropdown ids and stable values are converted to the copy sent to flex-core.
// Unknown text still passes through for compatibility with older modal state.
export const reasonLabelFor = (reason: CancelReason): string | null | undefined =>
  reason === null
    ? reason
    : reasonOptionFor(reason)?.label ?? (typeof reason === 'string' ? reason : undefined)

export const reasonValueFor = (reason: CancelReason): string | null | undefined =>
  reason === null
    ? reason
    : reasonOptionFor(reason)?.value ?? (typeof reason === 'string' ? reason : undefined)

export const toggleInList = (ids: number[], id: number) =>
  ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id]

export const isLateBooking = (booking: WorkerJobBooking, nowMs = Date.now()) => {
  const timeUntilStart = startMsOf(booking) - nowMs

  return timeUntilStart >= 0 && timeUntilStart < WINDOW_24H_MS
}

export const reasonDefaultsCH = (reason: CancelReason) => {
  const value = reasonValueFor(reason)

  return typeof value === 'string' && CH_DEFAULT_REASON_VALUES.includes(value)
}

export const buildCompensationDecisions = (
  bookings: WorkerJobBooking[],
  reason: CancelReason,
  nowMs = Date.now(),
): BookingCompensationDecisions =>
  Object.fromEntries(
    bookings.map(booking => {
      // UK and US share this policy. Existing CH is preserved; otherwise the reason and
      // start time decide whether the modal should begin with CH selected.
      const chDefaulted =
        !booking.syft_unpaid_hours_compensation && reasonDefaultsCH(reason) && isLateBooking(booking, nowMs)

      return [
        booking.shift_id,
        {
          ch: booking.syft_unpaid_hours_compensation || chDefaulted,
          chDefaulted,
          fc: booking.syft_compensation,
        },
      ]
    }),
  )

export const hasCompensationChanges = (
  selectedShiftIDs: number[],
  current: BookingCompensationDecisions,
  saved: BookingCompensationDecisions,
) =>
  selectedShiftIDs.some(shiftID => {
    const currentDecision = current[shiftID]
    const savedDecision = saved[shiftID]

    if (!currentDecision) return false
    if (!savedDecision) return true

    return (
      currentDecision.ch !== savedDecision.ch ||
      currentDecision.fc !== savedDecision.fc ||
      currentDecision.fcReason !== savedDecision.fcReason ||
      currentDecision.fcApprover !== savedDecision.fcApprover
    )
  })

// Selected shifts with a known overbook cover result, paired with their booking —
// covered and uncovered alike, so the preview can say "no cover" as plainly as
// "X is first in line" rather than only surfacing the good-news case.
export const buildCoverPreview = (
  selected: number[],
  bookings: WorkerJobBooking[],
  overbookCover: Record<number, ShiftOverbookCover>,
) =>
  selected
    .map(shiftId => {
      const booking = bookings.find(b => b.shift_id === shiftId)
      const cover = overbookCover[shiftId]

      return booking && cover ? { booking, cover } : null
    })
    .filter((x): x is { booking: WorkerJobBooking; cover: ShiftOverbookCover } => x !== null)

// The cancel request body. Only attaches the free-text custom reason when the selected
// reason is actually an "Other" option — otherwise a stale value (typed, then switched
// away from "Other") would leak into the payload.
export const buildCancelPayload = ({
  workerID,
  jobID,
  shiftIDs,
  currentShiftID,
  reason,
  reasonLabel,
  otherReason,
  deleteSpot,
}: {
  workerID?: number
  jobID?: number
  shiftIDs: number[]
  currentShiftID?: number
  reason: CancelReason
  reasonLabel?: string | null
  otherReason?: string
  // Forces flex-core's CancelBooking down the spot-removal path (cancellation_reason:
  // spot_removed) instead of reopening the shift — see SpotDecision in types.ts. Must
  // stay a single call: reopening first and shrinking the spot after races against the
  // async overbook-promotion reaction the reopen triggers (STSMPD-2072).
  deleteSpot?: boolean
}) => ({
  worker_id: workerID,
  job_id: jobID,
  shift_ids: [...shiftIDs],
  // shift_id scopes the post-cancel bookings refresh to the table's current shift.
  shift_id: currentShiftID,
  user_provided_reason: reasonLabel ?? reason,
  user_provided_custom_reason: isOtherReason(reasonLabel ?? reason) ? otherReason || null : null,
  delete_spot: !!deleteSpot,
})

// Loads the worker's cancellable bookings for the dialog's current target and pre-ticks
// the clicked shift, returning the effect cleanup (or undefined when there is nothing to
// load). Module level so the container's own logic stays readable; all state still lives
// in the container.
export const loadCancellableBookings = ({
  isShown,
  workerID,
  jobID,
  currentShiftID,
  resetDecisions,
  setBookings,
  setSelected,
  setLoading,
  setLoadError,
}: {
  isShown: boolean
  workerID?: number
  jobID?: number
  currentShiftID?: number
  resetDecisions: () => void
  setBookings: (bookings: WorkerJobBooking[]) => void
  setSelected: (shiftIDs: number[]) => void
  setLoading: (loading: boolean) => void
  setLoadError: (error?: string) => void
}) => {
  if (!isShown || workerID == null || jobID == null) {
    return undefined
  }
  let cancelled = false
  // Every decision was taken against the previous target, so none of them survive it.
  resetDecisions()
  setLoading(true)
  setLoadError(undefined)
  fetchCancellableBookings({ workerID, jobID })
    .then((res: { payload: WorkerJobBooking[] }) => {
      if (cancelled) return
      // handleCall resolves an envelope { payload, response, meta } — the bookings
      // array is on `payload` (see any direct api/resources consumer, e.g. DataEmployerVenue).
      const list = Array.isArray(res?.payload) ? res.payload : []
      setBookings(list)
      // Pre-tick the clicked shift if the worker is still booked on it.
      const match = list.find(b => Number(b.shift_id) === Number(currentShiftID))
      setSelected(match ? [match.shift_id] : [])
      setLoading(false)
    })
    .catch(() => {
      if (cancelled) return
      setLoadError('Could not load this worker’s bookings. Please close and try again.')
      setLoading(false)
    })

  return () => {
    cancelled = true
  }
}

// Refreshes the overbook-cover preview for the current selection. Any prior result is
// dropped up front — otherwise, while this request is in flight, a stale cover for a
// shift still in the new selection would keep rendering as if it were current.
// Best-effort: a failure here just shows nothing rather than blocking the cancellation
// the preview is describing.
export const loadOverbookCover = ({
  isShown,
  shiftIDs,
  setOverbookCover,
}: {
  isShown: boolean
  shiftIDs: number[]
  setOverbookCover: (cover: Record<number, ShiftOverbookCover>) => void
}) => {
  setOverbookCover({})
  if (!isShown || shiftIDs.length === 0) {
    return undefined
  }
  let cancelled = false
  fetchShiftOverbookCover({ shiftIDs })
    .then((res: { payload: ShiftOverbookCover[] }) => {
      if (cancelled) return
      const list = Array.isArray(res?.payload) ? res.payload : []
      setOverbookCover(Object.fromEntries(list.map(c => [c.shift_id, c])))
    })
    .catch(() => {
      if (!cancelled) setOverbookCover({})
    })

  return () => {
    cancelled = true
  }
}

// Footer submit label for the current step and spot decision.
export const submitLabel = ({
  step,
  count,
  spotDecision,
}: {
  step: CancelStep
  count: number
  spotDecision: SpotDecision
}) => {
  if (step === 'select') {
    return 'Next'
  }
  if (spotDecision === 'delete') {
    return `Cancel & remove ${count === 1 ? 'spot' : 'spots'}`
  }

  return `Cancel ${count} ${count === 1 ? 'shift' : 'shifts'}`
}
